import React, { useState } from 'react';
import {
	CSSZSickLeaveCertificate,
	CSSZUser,
} from '../../Interfaces/CSSZ.interface';
import styles from './CSSZSickLeaveCertificateDetailControls.module.scss';
import IconButton from '../../../Base/Components/IconButton/IconButton';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import useApiClient from '../../../Base/Hooks/useApiClient';
import apiClient from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import CSSZSickLeaveCertificateAttendingDoctorReportModal from '../CSSZSickLeaveCertificateAttendingDoctorReport/CSSZSickLeaveCertificateAttendingDoctorReportModal';
import CSSZSickLeaveCertificateConfirmationOfDurationModal from '../CSSZSickLeaveCertificateConfirmationOfDuration/CSSZSickLeaveCertificateConfirmationOfDurationModal';
import CSSZSickLeaveCertificateReturnAdmissionRequestModal from '../CSSZSickLeaveCertificateReturnAdmissionRequest/CSSZSickLeaveCertificateReturnAdmissionRequestModal';
import ContextMenu, {
	ContextMenuParams,
} from '../../../../Components/ContextMenu/ContextMenu';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import CSSZSickLeaveCertificateRequestForChooseOutingsTimeModal from '../CSSZSickLeaveCertificateRequestForChooseOutingsTime/CSSZSickLeaveCertificateRequestForChooseOutingsTimeModal';
import CSSZSickLeaveCertificateRequestForChangeAddressBecauseBeingOutsideOfCountryModal from '../CSSZSickLeaveCertificateRequestForChangeAddressBecauseBeingOutsideOfCountry/CSSZSickLeaveCertificateRequestForChangeAddressBecauseBeingOutsideOfCountryModal';
import CSSZSickLeaveCertificateEndOfSickLeaveModal from '../CSSZSickLeaveCertificateEndOfSickLeave/CSSZSickLeaveCertificateEndOfSickLeaveModal';
import RemoveConfirmationModal from '../../../../Components/RemoveConfirmation/RemoveConfirmationModal';
import CSSZSickLeaveCertificateRepairModal from '../CSSZSickLeaveCertificateRepair/CSSZSickLeaveCertificateRepairModal';
import CSSZSickLeaveCertificateSubmissionsOverviewModal from '../CSSZSickLeaveCertificateSubmissionsOverview/CSSZSickLeaveCertificateSubmissionsOverviewModal';
import { CSSZSickLeaveValues } from '../CSSZSickLeaveCertificateCreate/CSSZSickLeaveCertificateCreate';
import { prepareDataForApi } from '../../Helpers/prepareSickLeave.helper';
import {
	isRefreshAllowed,
	SUBMISSION_STATUS_KEYS,
} from '../../Helpers/sickLeaveStatusMap.helper';

interface Props {
	item: CSSZSickLeaveCertificate;
	user: CSSZUser;
	onJobSuccess?: () => void;
}

const CSSZSickLeaveCertificateDetailControls: React.FC<Props> = ({
	item,
	user,
	onJobSuccess,
}) => {
	const patientId = user.id;
	const { displayError } = useApiClient();
	const [requestsContext, setRequestsContext] = useState<ContextMenuParams>({
		isOpen: false,
	});
	const [moreContext, setMoreContext] = useState<ContextMenuParams>({
		isOpen: false,
	});
	const {
		isOpen: isConfirmationOfDurationOpen,
		onOpen: onOpenConfirmationOfDuration,
		onClose: onCloseConfirmationOfDuration,
	} = useDisclose();
	const {
		isOpen: isAttendingDoctorReportOpen,
		onOpen: onOpenAttendingDoctorReport,
		onClose: onCloseAttendingDoctorReport,
	} = useDisclose();
	const {
		isOpen: isReturnAdmissionRequestOpen,
		onOpen: onOpenReturnAdmissionRequest,
		onClose: onCloseReturnAdmissionRequest,
	} = useDisclose();
	const {
		isOpen: isRequestForChooseOutingsTimeOpen,
		onOpen: onOpenRequestForChooseOutingsTime,
		onClose: onCloseRequestForChooseOutingsTime,
	} = useDisclose();
	const {
		isOpen: isRequestForChangeAddressBecauseBeingOutsideOfCountryOpen,
		onOpen: onOpenRequestForChangeAddressBecauseBeingOutsideOfCountry,
		onClose: onCloseRequestForChangeAddressBecauseBeingOutsideOfCountry,
	} = useDisclose();
	const {
		isOpen: isEndOfSickLeaveOpen,
		onOpen: onOpenEndOfSickLeave,
		onClose: onCloseEndOfSickLeave,
	} = useDisclose();
	const {
		isOpen: isCancelConfirmationOpen,
		onOpen: onOpenCancelConfirmation,
		onClose: onCloseCancelConfirmation,
	} = useDisclose();
	const {
		isOpen: isRepairOpen,
		onOpen: onOpenRepair,
		onClose: onCloseRepair,
	} = useDisclose();
	const {
		isOpen: isSubmissionsOverviewOpen,
		onOpen: onOpenSubmissionsOverview,
		onClose: onCloseSubmissionsOverview,
	} = useDisclose();
	const { showNotificationSuccess } = useNotification();

	const openRequestsContext = (event: React.MouseEvent<HTMLDivElement>) => {
		setRequestsContext({
			isOpen: true,
			clientX: event.clientX,
			clientY: event.clientY,
		});
	};

	const closeRequestsContext = () => {
		setRequestsContext({ isOpen: false });
	};

	const openMoreContext = (event: React.MouseEvent<HTMLDivElement>) => {
		setMoreContext({
			isOpen: true,
			clientX: event.clientX,
			clientY: event.clientY,
		});
	};

	const closeMoreContext = () => {
		setMoreContext({ isOpen: false });
	};

	const handleSendToCSSZAgain = async () => {
		if (!isRefreshAllowed(item.status)) {
			return;
		}

		try {
			const response = await apiClient.post(
				`/user/${patientId}/sick-leave/${item.id}/resend`,
				{},
			);
			if (response.status === 200) {
				showNotificationSuccess({
					content:
						'component.CSSZSickLeaveCertificateDetailControls.resendSuccess',
				});
				onJobSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
	};

	const handleSubmitStoredToCssz = async () => {
		try {
			const response = await apiClient.post(
				`/user/${patientId}/sick-leave/${item.id}/submit-to-cssz`,
				{},
			);

			if (response.status === 200) {
				showNotificationSuccess({
					content:
						'component.CSSZSickLeaveCertificateDetailControls.sendAgainSuccess',
				});
				onJobSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
	};

	const handlePrint = async () => {
		try {
			const response = await apiClient.get(
				`/user/${patientId}/sick-leave/${item.id}/print`,
			);
			if (response.status === 200) {
				//TODO: TBA
			}
		} catch (e) {
			displayError(e);
		}
		closeMoreContext();
	};

	const handleCancel = async () => {
		try {
			const response = await apiClient.delete(
				`/user/${patientId}/sick-leave/${item.id}`,
			);
			if (response.status === 200) {
				showNotificationSuccess({
					content:
						'component.CSSZSickLeaveCertificateDetailControls.deleteSuccess',
				});
				onJobSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
		onCloseCancelConfirmation();
	};

	const handleRefresh = async () => {
		try {
			const response = await apiClient.post(
				`/user/${patientId}/sick-leave/${item.id}/refresh`,
				{},
			);
			if (response.status === 200) {
				showNotificationSuccess({
					content:
						'component.CSSZSickLeaveCertificateDetailControls.refreshSuccess',
				});
				onJobSuccess?.();
			}
		} catch (e) {
			displayError(e);
		}
		closeMoreContext();
	};

	const handleRepair = async (values: CSSZSickLeaveValues) => {
		try {
			const apiValues = prepareDataForApi(values);

			const response = await apiClient.patch(
				`/user/${patientId}/sick-leave/${item.id}/`,
				apiValues,
			);

			if (response.status === 200) {
				showNotificationSuccess({
					content:
						'component.CSSZSickLeaveCertificateDetailControls.repairSuccess',
				});
				onJobSuccess?.();
				onCloseRepair();
			}
		} catch (e) {
			displayError(e);
		}
	};

	const handleCloseRepair = () => {
		onCloseRepair();
		closeMoreContext();
	};

	const handleOpenRepair = () => {
		onOpenRepair();
		closeMoreContext();
	};

	const handleOpenSubmissionsOverview = () => {
		onOpenSubmissionsOverview();
		closeMoreContext();
	};

	const isCsszNotSentOnlyControls =
		item.status === SUBMISSION_STATUS_KEYS.CSSZ_NOT_SENT;

	const isSendAgainAllowed = isRefreshAllowed(item.status);

	return (
		<div className={styles.wrapper}>
			{isCsszNotSentOnlyControls ? (
				<>
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/sendAgain.svg'
						onClick={handleSubmitStoredToCssz}
						tooltip='base.shared.form.send'
					/>
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/trash.svg'
						onClick={onOpenCancelConfirmation}
						tooltip='component.CSSZSickLeaveCertificateDetailControls.cancel'
					/>
				</>
			) : (
				<>
					{isSendAgainAllowed && (
						<IconButton
							icon='/dist/icons/CSSZSickLeaveCertificate/sendAgain.svg'
							onClick={handleSendToCSSZAgain}
							tooltip='component.CSSZSickLeaveCertificateDetailControls.sendAgain'
						/>
					)}
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/hol.svg'
						onClick={onOpenAttendingDoctorReport}
						tooltip='component.CSSZSickLeaveCertificateDetailControls.hol'
					/>
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/money.svg'
						onClick={onOpenConfirmationOfDuration}
						tooltip='component.CSSZSickLeaveCertificateDetailControls.confirmation'
					/>
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/fileQuestionMark.svg'
						onClick={openRequestsContext}
						tooltip='component.CSSZSickLeaveCertificateDetailControls.requests'
					/>
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/leavingHouse.svg'
						onClick={onOpenEndOfSickLeave}
						tooltip='component.CSSZSickLeaveCertificateDetailControls.end'
					/>
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/trash.svg'
						onClick={onOpenCancelConfirmation}
						tooltip='component.CSSZSickLeaveCertificateDetailControls.cancel'
					/>
					<IconButton
						icon='/dist/icons/CSSZSickLeaveCertificate/more.svg'
						onClick={openMoreContext}
						tooltip='component.CSSZSickLeaveCertificateDetailControls.more'
					/>
				</>
			)}
			{isConfirmationOfDurationOpen && (
				<CSSZSickLeaveCertificateConfirmationOfDurationModal
					isOpen
					item={item}
					patientId={patientId}
					onClose={onCloseConfirmationOfDuration}
					onCreate={() => {
						onCloseConfirmationOfDuration();
						onJobSuccess?.();
					}}
				/>
			)}
			{isAttendingDoctorReportOpen && (
				<CSSZSickLeaveCertificateAttendingDoctorReportModal
					isOpen
					item={item}
					patientId={patientId}
					onClose={onCloseAttendingDoctorReport}
					onCreate={() => {
						onCloseAttendingDoctorReport();
						onJobSuccess?.();
					}}
				/>
			)}
			{isReturnAdmissionRequestOpen && (
				<CSSZSickLeaveCertificateReturnAdmissionRequestModal
					isOpen
					item={item}
					patientId={patientId}
					onClose={onCloseReturnAdmissionRequest}
					onCreate={() => {
						onCloseReturnAdmissionRequest();
						onJobSuccess?.();
					}}
				/>
			)}
			{isRequestForChooseOutingsTimeOpen && (
				<CSSZSickLeaveCertificateRequestForChooseOutingsTimeModal
					isOpen
					item={item}
					patientId={patientId}
					onClose={onCloseRequestForChooseOutingsTime}
					onCreate={() => {
						onCloseRequestForChooseOutingsTime();
						onJobSuccess?.();
					}}
				/>
			)}
			{isRequestForChangeAddressBecauseBeingOutsideOfCountryOpen && (
				<CSSZSickLeaveCertificateRequestForChangeAddressBecauseBeingOutsideOfCountryModal
					isOpen
					item={item}
					patientId={patientId}
					onClose={onCloseRequestForChangeAddressBecauseBeingOutsideOfCountry}
					onCreate={() => {
						onCloseRequestForChangeAddressBecauseBeingOutsideOfCountry();
						onJobSuccess?.();
					}}
				/>
			)}
			{isEndOfSickLeaveOpen && (
				<CSSZSickLeaveCertificateEndOfSickLeaveModal
					isOpen
					item={item}
					user={user}
					onClose={onCloseEndOfSickLeave}
					onCreate={() => {
						onCloseEndOfSickLeave();
						onJobSuccess?.();
					}}
				/>
			)}
			{isCancelConfirmationOpen && (
				<RemoveConfirmationModal
					opened
					onConfirm={handleCancel}
					onClose={onCloseCancelConfirmation}
					confirmationTitle='component.CSSZSickLeaveCertificateCancel.title'
					confirmationText='component.CSSZSickLeaveCertificateCancel.text'
				/>
			)}
			{isRepairOpen && (
				<CSSZSickLeaveCertificateRepairModal
					isOpen
					onSubmit={handleRepair}
					documentId={item.id}
					user={user}
					onClose={handleCloseRepair}
				/>
			)}
			{isSubmissionsOverviewOpen && (
				<CSSZSickLeaveCertificateSubmissionsOverviewModal
					isOpen
					submissions={item.submissions ?? []}
					onClose={onCloseSubmissionsOverview}
				/>
			)}
			{!isCsszNotSentOnlyControls && (
				<ContextMenu
					params={requestsContext}
					onContextClose={closeRequestsContext}
					openInModalRoot={true}
					customRootName='context-menu-root'>
					<LinkButton
						title='component.CSSZSickLeaveCertificateDetailControls.returnAdmissionRequest'
						theme={Theme.BLUE}
						onClick={() => {
							closeRequestsContext();
							onOpenReturnAdmissionRequest();
						}}
					/>
					<LinkButton
						title='component.CSSZSickLeaveCertificateDetailControls.requestForChooseOutingsTime'
						theme={Theme.BLUE}
						onClick={() => {
							closeRequestsContext();
							onOpenRequestForChooseOutingsTime();
						}}
					/>
					<LinkButton
						title='component.CSSZSickLeaveCertificateDetailControls.requestForChangeAddressBecauseBeingOutsideOfCountry'
						theme={Theme.BLUE}
						onClick={() => {
							closeRequestsContext();
							onOpenRequestForChangeAddressBecauseBeingOutsideOfCountry();
						}}
					/>
				</ContextMenu>
			)}
			{!isCsszNotSentOnlyControls && (
				<ContextMenu
					params={moreContext}
					onContextClose={closeMoreContext}
					openInModalRoot={true}
					customRootName='context-menu-root'>
					<LinkButton
						title='component.CSSZSickLeaveCertificateDetailControls.print'
						theme={Theme.BLUE}
						onClick={handlePrint}
					/>
					<LinkButton
						title='component.CSSZSickLeaveCertificateDetailControls.repairPrimaryPN'
						theme={Theme.BLUE}
						onClick={handleOpenRepair}
					/>
					<LinkButton
						title='component.CSSZSickLeaveCertificateDetailControls.refetchActualState'
						theme={Theme.BLUE}
						onClick={handleRefresh}
					/>
					<LinkButton
						title='component.CSSZSickLeaveCertificateDetailControls.submissionOverview'
						theme={Theme.BLUE}
						onClick={handleOpenSubmissionsOverview}
					/>
				</ContextMenu>
			)}
		</div>
	);
};

export default CSSZSickLeaveCertificateDetailControls;
